import axios from 'axios';

import { APIResponseParsed } from 'types/response';
import { postRequest } from 'utils/HttpRequest/http-request-utils';
import { NativeDeviceToken } from 'utils/native-container/deviceToken';

import { parseAxiosError } from './utils/parse-axios-error';

// ==============================
// REQUESTS
// ==============================

/**
 * Register this handset to receive push notifications for the signed-in user.
 *
 * The backend is a thin hop to `app-notification-service`, and the platform
 * calls it rather than the app because the app holds no credentials: the
 * session lives here, inside the WebView (Mitch, 2026-09-18).
 *
 * **No user id is sent.** The backend takes it from the verified bearer token,
 * so a field here would be a claim rather than proof, and honouring one would
 * let anyone register a device against any account and receive that farm's
 * alerts.
 *
 * Idempotent. The container re-injects its token on every page load, because a
 * reload wipes anything injected, so a repeat is ordinary running rather than a
 * conflict.
 */
async function registerDeviceToken(device: NativeDeviceToken): Promise<APIResponseParsed<null>> {
  try {
    await postRequest<void, NativeDeviceToken>('user-device-token/v1', device);
    return { data: null, error: null };
  } catch (error) {
    if (!axios.isAxiosError(error)) throw error;
    return { data: null, error: parseAxiosError(error) };
  }
}

/**
 * Stop this handset receiving notifications for the signed-in user.
 *
 * Called on sign-out, so a shared farm phone stops delivering the previous
 * user's alerts to whoever picks it up next. An unknown pairing is not an
 * error: signing out twice is ordinary.
 */
async function deregisterDeviceToken(device: NativeDeviceToken): Promise<APIResponseParsed<null>> {
  try {
    await postRequest<void, NativeDeviceToken>('user-device-token/v1/deregister', device);
    return { data: null, error: null };
  } catch (error) {
    if (!axios.isAxiosError(error)) throw error;
    return { data: null, error: parseAxiosError(error) };
  }
}

export default {
  registerDeviceToken,
  deregisterDeviceToken
};
